import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaSearch, 
  FaDownload, 
  FaPrint,
  FaBalanceScale,
  FaEye,
  FaChevronDown,
  FaChevronRight 
} from 'react-icons/fa';
import LoadingSpinner from '../common/LoadingSpinner';

const BalanceView = () => {
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState([]);
  const [exerciceActuel, setExerciceActuel] = useState(null);
  const [filtres, setFiltres] = useState({
    exercice: null,
    periode: null,
    classe: '',
    compteDebut: '',
    compteFin: '',
    masquerComptesVides: true,
    grouperParClasse: true
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedClasses, setExpandedClasses] = useState({});
  const [totaux, setTotaux] = useState({
    totalDebit: 0,
    totalCredit: 0,
    totalSoldeDebiteur: 0,
    totalSoldeCrediteur: 0
  });

  // Classes comptables OHADA
  const classesOhada = [
    { code: '1', libelle: 'Comptes de ressources durables' },
    { code: '2', libelle: 'Comptes d\'immobilisations' },
    { code: '3', libelle: 'Comptes de stocks' },
    { code: '4', libelle: 'Comptes de tiers' },
    { code: '5', libelle: 'Comptes de trésorerie' },
    { code: '6', libelle: 'Comptes de charges' },
    { code: '7', libelle: 'Comptes de produits' },
    { code: '8', libelle: 'Comptes de résultats' },
    { code: '9', libelle: 'Comptes analytiques' }
  ];

  // Fonction de calcul des totaux (définie avant d'être utilisée dans useCallback)
  const calculerTotaux = useCallback((balanceData) => {
    const totaux = balanceData.reduce((acc, item) => ({
      totalDebit: acc.totalDebit + item.total_debit,
      totalCredit: acc.totalCredit + item.total_credit,
      totalSoldeDebiteur: acc.totalSoldeDebiteur + (item.solde > 0 ? item.solde : 0),
      totalSoldeCrediteur: acc.totalSoldeCrediteur + (item.solde < 0 ? Math.abs(item.solde) : 0)
    }), {
      totalDebit: 0,
      totalCredit: 0,
      totalSoldeDebiteur: 0,
      totalSoldeCrediteur: 0
    });

    setTotaux(totaux);
  }, []);

  const loadExerciceActuel = useCallback(async () => {
    try {
      // const response = await comptabiliteService.getExerciceActuel();
      // setExerciceActuel(response.data);
      
      // Simulation
      setExerciceActuel({ annee: 2025, statut: 'ouvert' });
    } catch (error) {
      console.error('Erreur chargement exercice:', error);
    }
  }, []);

  // Correction: ajouter calculerTotaux dans les dépendances
  const loadBalance = useCallback(async () => {
    try {
      setLoading(true);
      
      // Simulation des données de balance
      const balanceSimulee = [
        {
          _id: '101',
          compte: { 
            _id: '1', 
            code: '101', 
            libelle: 'Capital social',
            classe_id: { _id: 'c1', code: '1', libelle: 'Comptes de ressources durables' }
          },
          total_debit: 0,
          total_credit: 5000000,
          solde: -5000000 // Créditeur
        },
        {
          _id: '411',
          compte: { 
            _id: '2', 
            code: '411', 
            libelle: 'Clients',
            classe_id: { _id: 'c4', code: '4', libelle: 'Comptes de tiers' }
          },
          total_debit: 2500000,
          total_credit: 1800000,
          solde: 700000 // Débiteur
        },
        {
          _id: '401',
          compte: { 
            _id: '3', 
            code: '401', 
            libelle: 'Fournisseurs',
            classe_id: { _id: 'c4', code: '4', libelle: 'Comptes de tiers' }
          },
          total_debit: 1200000,
          total_credit: 1800000,
          solde: -600000 // Créditeur
        },
        {
          _id: '512',
          compte: { 
            _id: '4', 
            code: '512', 
            libelle: 'Banque CBAO',
            classe_id: { _id: 'c5', code: '5', libelle: 'Comptes de trésorerie' }
          },
          total_debit: 3200000,
          total_credit: 2900000,
          solde: 300000 // Débiteur
        },
        {
          _id: '601',
          compte: { 
            _id: '5', 
            code: '601', 
            libelle: 'Achats de marchandises',
            classe_id: { _id: 'c6', code: '6', libelle: 'Comptes de charges' }
          },
          total_debit: 8500000,
          total_credit: 0,
          solde: 8500000 // Débiteur
        },
        {
          _id: '701',
          compte: { 
            _id: '6', 
            code: '701', 
            libelle: 'Ventes de marchandises',
            classe_id: { _id: 'c7', code: '7', libelle: 'Comptes de produits' }
          },
          total_debit: 0,
          total_credit: 12000000,
          solde: -12000000 // Créditeur
        }
      ];

      setBalance(balanceSimulee);
      calculerTotaux(balanceSimulee);
      
    } catch (error) {
      console.error('Erreur chargement balance:', error);
    } finally {
      setLoading(false);
    }
  }, [calculerTotaux]); // Ajout de calculerTotaux dans les dépendances

  useEffect(() => {
    loadExerciceActuel();
  }, [loadExerciceActuel]);

  useEffect(() => {
    if (exerciceActuel) {
      setFiltres(prev => ({ ...prev, exercice: exerciceActuel.annee }));
      loadBalance();
    }
  }, [exerciceActuel, loadBalance]);

  useEffect(() => {
    if (filtres.exercice) {
      loadBalance();
    }
  }, [filtres.exercice, filtres.periode, filtres.classe, loadBalance]);

  const toggleExpandClasse = (classeCode) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classeCode]: !prev[classeCode]
    }));
  };

  const balanceFiltree = balance.filter(item => {
    // Filtre par recherche
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!item.compte.code.toLowerCase().includes(search) && 
          !item.compte.libelle.toLowerCase().includes(search)) {
        return false;
      }
    }

    // Filtre par classe
    if (filtres.classe && item.compte.classe_id.code !== filtres.classe) {
      return false;
    }

    // Filtre par intervalle de comptes
    if (filtres.compteDebut && item.compte.code < filtres.compteDebut) {
      return false;
    }
    if (filtres.compteFin && item.compte.code > filtres.compteFin) {
      return false;
    }

    // Masquer les comptes vides
    if (filtres.masquerComptesVides && item.solde === 0) {
      return false;
    }

    return true;
  });

  const balanceGroupee = () => {
    if (!filtres.grouperParClasse) {
      return { 'Tous': balanceFiltree };
    }

    const groupes = {};
    balanceFiltree.forEach(item => {
      const classeCode = item.compte.classe_id.code;
      if (!groupes[classeCode]) {
        groupes[classeCode] = {
          classe: item.compte.classe_id,
          comptes: []
        };
      }
      groupes[classeCode].comptes.push(item);
    });

    return groupes;
  };

  const exporterBalance = () => {
    // Logique d'export Excel/PDF
    console.log('Export balance:', { filtres, balance: balanceFiltree });
    alert('Export en cours de développement');
  };

  if (loading) return <LoadingSpinner />;

  const groupes = balanceGroupee();

  return (
    <div className="space-y-6">
      {/* En-tête avec filtres */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
              <FaBalanceScale className="mr-3 text-primary-600" />
              Balance Comptable
            </h2>
            {exerciceActuel && (
              <p className="text-gray-600 mt-1">
                Exercice {exerciceActuel.annee} 
                {filtres.periode && ` - Période jusqu'à ${filtres.periode}/2025`}
              </p>
            )}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={exporterBalance}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <FaDownload />
              <span>Exporter</span>
            </button>
            
            <button
              onClick={() => window.print()}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <FaPrint />
              <span>Imprimer</span>
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Période (mois)
            </label>
            <select
              value={filtres.periode || ''}
              onChange={(e) => setFiltres(prev => ({ 
                ...prev, 
                periode: e.target.value ? parseInt(e.target.value) : null 
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Cumul annuel</option>
              {Array.from({length: 12}, (_, i) => i + 1).map(mois => (
                <option key={mois} value={mois}>
                  Jusqu'à {new Date(2025, mois - 1).toLocaleDateString('fr-FR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Classe comptable
            </label>
            <select
              value={filtres.classe}
              onChange={(e) => setFiltres(prev => ({ ...prev, classe: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Toutes les classes</option>
              {classesOhada.map(classe => (
                <option key={classe.code} value={classe.code}>
                  Classe {classe.code} - {classe.libelle}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Du compte
            </label>
            <input
              type="text"
              value={filtres.compteDebut}
              onChange={(e) => setFiltres(prev => ({ ...prev, compteDebut: e.target.value }))}
              placeholder="101"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Au compte
            </label>
            <input
              type="text"
              value={filtres.compteFin}
              onChange={(e) => setFiltres(prev => ({ ...prev, compteFin: e.target.value }))}
              placeholder="999"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Options et recherche */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filtres.masquerComptesVides}
                onChange={(e) => setFiltres(prev => ({ 
                  ...prev, 
                  masquerComptesVides: e.target.checked 
                }))}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Masquer les comptes à solde nul</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filtres.grouperParClasse}
                onChange={(e) => setFiltres(prev => ({ 
                  ...prev, 
                  grouperParClasse: e.target.checked 
                }))}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Grouper par classe</span>
            </label>
          </div>

          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un compte..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Résumé financier */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Débits</p>
              <p className="text-2xl font-bold text-blue-600">
                {totaux.totalDebit.toLocaleString()} XOF
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FaBalanceScale className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Crédits</p>
              <p className="text-2xl font-bold text-green-600">
                {totaux.totalCredit.toLocaleString()} XOF
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FaBalanceScale className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Soldes Débiteurs</p>
              <p className="text-2xl font-bold text-orange-600">
                {totaux.totalSoldeDebiteur.toLocaleString()} XOF
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <FaBalanceScale className="text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Soldes Créditeurs</p>
              <p className="text-2xl font-bold text-purple-600">
                {totaux.totalSoldeCrediteur.toLocaleString()} XOF
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FaBalanceScale className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Table de balance */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Libellé
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Débits
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Crédits
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Solde Débiteur
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Solde Créditeur
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(groupes).map(([classeCode, groupe]) => (
                <React.Fragment key={classeCode}>
                  {/* En-tête de classe (si groupé) */}
                  {filtres.grouperParClasse && classeCode !== 'Tous' && (
                    <tr className="bg-gray-100">
                      <td
                        colSpan={7}
                        className="px-6 py-4 cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => toggleExpandClasse(classeCode)}
                      >
                        <div className="flex items-center space-x-2">
                          {expandedClasses[classeCode] ? 
                            <FaChevronDown /> : 
                            <FaChevronRight />
                          }
                          <span className="font-semibold text-gray-800">
                            Classe {groupe.classe.code} - {groupe.classe.libelle}
                          </span>
                          <span className="text-sm text-gray-600">
                            ({groupe.comptes.length} comptes)
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {/* Lignes de comptes */}
                  {(!filtres.grouperParClasse || 
                    classeCode === 'Tous' || 
                    expandedClasses[classeCode]) && 
                    (groupe.comptes || groupe).map((item, index) => (
                    <tr 
                      key={item._id}
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.compte.code}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {item.compte.libelle}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {item.total_debit.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {item.total_credit.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                        {item.solde > 0 ? (
                          <span className="text-blue-600">
                            {item.solde.toLocaleString()}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                        {item.solde < 0 ? (
                          <span className="text-red-600">
                            {Math.abs(item.solde).toLocaleString()}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => {
                            // Navigation vers le grand livre de ce compte
                            console.log('Voir grand livre:', item.compte.code);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                          title="Voir le grand livre"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              
              {/* Ligne de totaux */}
              <tr className="bg-gray-800 text-white font-bold">
                <td className="px-6 py-4 text-sm">TOTAUX</td>
                <td className="px-6 py-4 text-sm"></td>
                <td className="px-6 py-4 text-sm text-right">
                  {totaux.totalDebit.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  {totaux.totalCredit.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  {totaux.totalSoldeDebiteur.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm text-right">
                  {totaux.totalSoldeCrediteur.toLocaleString()}
                </td>
                <td className="px-6 py-4"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {balanceFiltree.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Aucun compte trouvé avec les filtres appliqués
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceView;